import { CATEGORIES } from './languages/Categories';
import { de } from './languages/de';
import { en } from './languages/en';
import Fuse from 'fuse.js';

const DEFAULT_ICON = '/assets/default.webp';

// Dictionary of available language files
const TRANSLATIONS = { de, en };

// --- 1. SEARCH LIST GENERATOR ---
// Flatten the language maps into a searchable array:
// [{ key: 'APPLE', value: 'apfel' }, { key: 'APPLE', value: 'äpfel' }, ...]
const generateSearchList = (langLib) => {
  const list = [];
  for (const [categoryKey, keywords] of Object.entries(langLib)) {
    // Only map if category exists in CATEGORIES (safety check)
    // Note: The keys in de/en (e.g. 'apple') map to CATEGORIES keys ('APPLE') 
    // but CATEGORIES also has a 'key' prop ('apple') which matches the lang file key.

    // We find the CATEGORY object whose .key property matches the langLib key
    const categoryConfig = Object.values(CATEGORIES).find(c => c.key === categoryKey);

    if (categoryConfig && Array.isArray(keywords)) {
      keywords.forEach(kw => {
        list.push({
          categoryKey: Object.keys(CATEGORIES).find(key => CATEGORIES[key] === categoryConfig), // Get uppercase key 'APPLE'
          value: kw
        });
      });
    }
  }
  return list;
};

// --- Cache Fuse Instances ---
const fuseInstances = {};

const getFuseInstance = (lang) => {
  if (!fuseInstances[lang]) {
    const langLib = TRANSLATIONS[lang] || TRANSLATIONS.en;
    const searchList = generateSearchList(langLib);
    fuseInstances[lang] = new Fuse(searchList, {
      keys: ['value'],
      includeScore: true,
      threshold: 0.35, // Sensitivity: 0.0 = exact, 1.0 = anything
      ignoreLocation: true, // Search entire string, not just start
    });
  }
  return fuseInstances[lang];
};


/**
 * Detect the category key for a given item name.
 * @param {string} itemName 
 * @param {string} lang 
 * @returns {string|null} The category key (e.g. 'APPLE') or null if not found.
 */
export const getCategoryForItem = (itemName = '', lang = 'de') => {
  if (!itemName) return null;

  // Normalize language
  const baseLang = lang.split('-')[0].toLowerCase();
  const searchStr = itemName.toLowerCase().trim();
  const langLib = TRANSLATIONS[baseLang] || TRANSLATIONS.en;

  // 1. STRICT MATCH (Longest Keyword Wins strategy)
  let bestMatchCategoryKey = null;
  let maxKeywordLength = 0;

  for (const [catKey, config] of Object.entries(CATEGORIES)) {
    const keywords = langLib[config.key]; // config.key is the translation lookup key (e.g. 'apple')
    if (keywords) {
      for (const kw of keywords) {
        if (searchStr.includes(kw.toLowerCase())) {
          if (kw.length > maxKeywordLength) {
            maxKeywordLength = kw.length;
            bestMatchCategoryKey = catKey; // 'APPLE'
          }
        }
      }
    }
  }

  if (bestMatchCategoryKey) return bestMatchCategoryKey;

  // 2. FUZZY SEARCH (Fallback)
  const fuse = getFuseInstance(baseLang);
  const results = fuse.search(searchStr);

  if (results.length > 0) {
    return results[0].item.categoryKey;
  }

  return null;
};

/**
 * Globalized Icon Finder with Fuzzy Matching
 * @param {string} itemName - The text from the shopping list
 * @param {string} lang - 'en', 'de', etc. (Defaults to 'de')
 */
export const getIconForItem = (itemName = '', lang = 'de') => {
  const categoryKey = getCategoryForItem(itemName, lang);
  if (categoryKey && CATEGORIES[categoryKey]) {
    return CATEGORIES[categoryKey].icon;
  }
  return DEFAULT_ICON;
};

/**
 * Get fuzzy suggestions for item creation
 * @param {string} query 
 * @param {string} lang 
 * @returns {Array<{name: string, icon: string, categoryKey: string}>}
 */
export const getItemSuggestions = (query = '', lang = 'de') => {
  if (!query || query.length < 2) return [];

  const baseLang = lang.split('-')[0].toLowerCase();
  const fuse = getFuseInstance(baseLang);
  const results = fuse.search(query.trim());

  // Map Fuse results to usable suggestions
  // fuse results: [ { item: { categoryKey: 'APPLE', value: 'apfel' }, score: 0.1, ... }, ... ]
  return results.slice(0, 10).map(res => {
    const catConfig = CATEGORIES[res.item.categoryKey];
    const rawName = res.item.value; // localized keyword e.g. "apfel"
    const capitalizedName = rawName.charAt(0).toUpperCase() + rawName.slice(1);

    return {
      name: capitalizedName,
      icon: catConfig ? catConfig.icon : DEFAULT_ICON,
      categoryKey: res.item.categoryKey
    };
  });
};

/**
 * Returns a color hex code based on category sortOrder
 * @param {string} itemName 
 * @param {string} lang 
 * @returns {string} hex color code
 */
export const getGroupColor = (itemName = '', lang = 'de') => {
  const categoryKey = getCategoryForItem(itemName, lang);
  const so = categoryKey && CATEGORIES[categoryKey] ? CATEGORIES[categoryKey].sortOrder : 999;

  if (so < 110) return "#42A5F5"; // Fruits - Blue 400
  if (so < 200) return "#26C6DA"; // Vegetables - Cyan 400
  if (so < 300) return "#5C6BC0"; // Bakery - Indigo 400
  if (so < 400) return "#7986CB"; // Meat & Fish - Indigo 300
  if (so < 500) return "#29B6F6"; // Dairy - Light Blue 400
  if (so < 600) return "#78909C"; // Pantry - Blue Grey 400
  if (so < 700) return "#4DD0E1"; // Snacks - Cyan 300
  if (so < 800) return "#1E88E5"; // Beverages - Blue 600
  if (so < 900) return "#546E7A"; // Household - Blue Grey 600
  return "#90A4AE"; // Other - Blue Grey 300
};